import { useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../hooks/useNotification";
import {
  useGetLegalCompliance,
  useGetLegalDates,
  useGetUserPreferences,
} from "../api/queries";
import {
  useUpdateLegalAcceptance,
  useUpdateUserPreferences,
} from "../api/mutations";
import { LegalComplianceStatus } from "../api/types";
import { useAuth } from "../contexts/AuthContext";

export const useLegalComplianceGuard = (shouldCheck: boolean = true) => {
  const [showModal, setShowModal] = useState(false);
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { logout: authLogout } = useAuth();
  const {
    data: rawComplianceStatus,
    isLoading: checkingCompliance,
    isError: isComplianceStatusError,
  } = useGetLegalCompliance(shouldCheck);

  const {
    data: legalDates,
    isLoading: loadingLegalDates,
    isError: isLegalDatesError,
  } = useGetLegalDates();

  const {
    data: userPreferences,
    isLoading: loadingUserPreferences,
    isError: isUserPreferencesError,
  } = useGetUserPreferences();

  const complianceStatus: LegalComplianceStatus | undefined =
    rawComplianceStatus && legalDates
      ? {
          needsAcceptance: rawComplianceStatus.needsAcceptance,
          lastUpdated: new Date(rawComplianceStatus.lastUpdated),
          documentDates: {
            termsLastUpdated: new Date(legalDates.termsLastUpdated),
            privacyLastUpdated: new Date(legalDates.privacyLastUpdated),
            eulaLastUpdated: new Date(legalDates.eulaLastUpdated),
          },
          documentAcceptanceStatus: {
            termsAccepted:
              rawComplianceStatus.documentAcceptanceStatus.termsAccepted,
            privacyAccepted:
              rawComplianceStatus.documentAcceptanceStatus.privacyAccepted,
            eulaAccepted:
              rawComplianceStatus.documentAcceptanceStatus.eulaAccepted,
            termsAcceptedDate: rawComplianceStatus.documentAcceptanceStatus
              .termsAcceptedDate
              ? new Date(
                  rawComplianceStatus.documentAcceptanceStatus.termsAcceptedDate,
                )
              : undefined,
            privacyAcceptedDate: rawComplianceStatus.documentAcceptanceStatus
              .privacyAcceptedDate
              ? new Date(
                  rawComplianceStatus.documentAcceptanceStatus.privacyAcceptedDate,
                )
              : undefined,
            eulaAcceptedDate: rawComplianceStatus.documentAcceptanceStatus
              .eulaAcceptedDate
              ? new Date(
                  rawComplianceStatus.documentAcceptanceStatus.eulaAcceptedDate,
                )
              : undefined,
          },
        }
      : undefined;

  const legalAcceptanceMutation = useUpdateLegalAcceptance();
  const updateUserPreferencesMutation = useUpdateUserPreferences({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["userPreferences"] });
    },
    onError: () => {
      notify(
        "error",
        "Failed to update email preference. Please check your profile settings.",
      );
    },
  });

  useEffect(() => {
    if (shouldCheck && complianceStatus && userPreferences) {
      setShowModal(
        complianceStatus.needsAcceptance ||
          userPreferences.emailAccountIssues === undefined ||
          userPreferences.emailFeedbackRequests === undefined ||
          userPreferences.emailNewsUpdates === undefined,
      );
    }
  }, [complianceStatus, shouldCheck, userPreferences]);

  const handleAccept = useCallback(
    (
      emailAccountIssues: boolean,
      emailFeedbackRequests: boolean,
      emailNewsUpdates: boolean,
    ) => {
      if (!legalDates) return;

      legalAcceptanceMutation.mutate(
        {
          acceptLegal: true,
          version: legalDates.termsVersion, // Use server-provided version
          agreedToEmail:
            emailAccountIssues || emailFeedbackRequests || emailNewsUpdates,
        },
        {
          onSuccess: () => {
            updateUserPreferencesMutation.mutate({
              saveAnswersToLog: userPreferences?.saveAnswersToLog ?? true,
              emailAccountIssues,
              emailFeedbackRequests,
              emailNewsUpdates,
            });

            setShowModal(false);
            notify(
              "success",
              "Legal documents accepted successfully! Welcome back.",
            );
          },
          onError: (error) => {
            const errorMessage =
              error?.message ||
              "An error occurred while accepting legal documents.";
            notify("error", `${errorMessage} Please try again.`);
          },
        },
      );
    },
    [
      legalAcceptanceMutation,
      updateUserPreferencesMutation,
      notify,
      legalDates,
      userPreferences?.saveAnswersToLog,
    ],
  );

  const handleDecline = useCallback(async () => {
    await authLogout();
    navigate("/login", { replace: true });
  }, [authLogout, navigate]);

  useEffect(() => {
    if (
      shouldCheck &&
      (isComplianceStatusError || isLegalDatesError || isUserPreferencesError)
    ) {
      authLogout().then(() => {
        navigate("/login", { replace: true });
      });
    }
  }, [
    shouldCheck,
    isComplianceStatusError,
    isLegalDatesError,
    isUserPreferencesError,
    navigate,
    notify,
    authLogout,
  ]);

  return {
    showModal,
    complianceStatus,
    isLoading:
      checkingCompliance || loadingLegalDates || loadingUserPreferences,
    handleAccept,
    handleDecline,
    isAccepting: legalAcceptanceMutation.isPending,
    acceptanceError: legalAcceptanceMutation.error,
  };
};
